import { type IOrderStatus } from "@commonTypes/orderStatus"
import { AxiosError } from "axios"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { Button } from "@components/ui/button"
import { useUpdateOrderStatus } from "@apis/orders/useOrders"
import {
  useGetOrderStatusesQuery,
  useGetOrderReasonsQuery,
} from "@apis/shared/useShared"

type UpdateOrderStatusFormProps = {
  order_id: number
  onSubmitSuccess?: () => void
  onCancel?: () => void
}

export const UpdateOrderStatus = ({
  order_id,
  onSubmitSuccess,
  onCancel,
}: UpdateOrderStatusFormProps) => {
  const form = useForm<IOrderStatus>({
    mode: "onTouched",
  })

  const { mutate, isPending } = useUpdateOrderStatus()

  const { data: orderStatuses = [] } = useGetOrderStatusesQuery()
  const { data: orderReasons = [] } = useGetOrderReasonsQuery()

  const handleUpdateStatus = (formData: IOrderStatus) => {
    mutate(
      {
        order_id,
        body: {
          order_status: formData.order_status,
          order_status_reason: formData.order_status_reason,
        },
      },
      {
        onSuccess: () => {
          toast.success("Order Status successfully Updated!")
          onSubmitSuccess?.()
        },
        onError: (error: string | AxiosError) =>
          toast.error(`Error updating status! ${error}`),
      },
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()} className="tw:space-y-6">
        <div className="tw:grid tw:grid-cols-1 tw:gap-4">
          <FormField
            control={form.control}
            name="order_status"
            rules={{ required: "Status is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl className="tw:w-full">
                    <SelectTrigger>
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {orderStatuses.map(
                      (option: { value: string; label: string }) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="order_status_reason"
            rules={{ required: "Reason is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reason</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl className="tw:w-full">
                    <SelectTrigger>
                      <SelectValue placeholder="Select Reason" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {orderReasons.map(
                      (option: { value: string; label: string }) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="tw:flex tw:justify-end tw:gap-4">
          <Button
            type="button"
            disabled={!form.formState.isDirty || isPending}
            onClick={form.handleSubmit(handleUpdateStatus)}
          >
            Ok
          </Button>
          <Button type="button" variant="outline" onClick={() => onCancel?.()}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  )
}
